<script lang="ts" setup>
import { computed, onMounted, ref, toRefs } from 'vue';
import useVuelidate from '@vuelidate/core';
import { email, required, requiredIf } from '@vuelidate/validators';
import Dialog from 'primevue/dialog';
import TreeSelect from 'primevue/treeselect';
import AppSelectButton from '@/components/AppSelectButton/AppSelectButton.vue';
import { EMIT, PERMISSIONS } from '@/enum';
import { useDateTime, usePersonTypes, useRouting, can } from '@/composables';
import {
  usePeopleStore,
  useAuthStore,
  usePeopleTypesStore,
  useTimezoneStore,
  useOrganizationStore,
  PersonStores,
} from '@/store';
import { ApiPromise, GeneralProfileInterface, OrganizationInterface, PersonCreateInterface } from '@/types';

/**
 * Define a routing instance for the utility.
 */
const $routing = useRouting();

/**
 * Define the store instances.
 */
const peopleStore = usePeopleStore();
const peopleTypesStore = usePeopleTypesStore();
const timezoneStore = useTimezoneStore();
const orgStore = useOrganizationStore();
const profileStore = PersonStores.useUserProfileStore();
const authStore = useAuthStore();
const PEOPLE_TYPES = peopleTypesStore.PEOPLE_TYPES;
const $externalResults = ref({});

interface UserCreatePropsInterface {
  isEdit?: boolean;
  isAdmin?: boolean;
  id?: number | null;
  personTypeAcronym?: string;
  header?: string;
  saveButtonText?: string;
  isProfile?: boolean;
}

const props = withDefaults(defineProps<UserCreatePropsInterface>(), {
  isEdit: false,
  isAdmin: false,
  id: null,
  header: 'Create User',
  personTypeAcronym: '',
  saveButtonText: 'Create User',
  isProfile: false,
});

const { isEdit, isAdmin, id, header, personTypeAcronym, saveButtonText, isProfile } = toRefs(props);

/**
 * Define all the available emit events.
 */
const emit = defineEmits([EMIT.CANCEL, EMIT.EDIT]);

/**
 * Whether or not to display the user create modal.
 */
const displayModal = ref(true);

/**
 * for the user types we only need for the tabs
 */
const userTypes = ref();

/**
 * support admin types
 */
const supportAdminUserTypes = ref([{ label: 'Support Admin', id: PEOPLE_TYPES.SUPPORT_ADMIN }]);

/**
 * super admin types
 */
const superAdminUserTypes = ref([{ label: 'Super Admin', id: PEOPLE_TYPES.SUPER_ADMIN }]);

/**
 * non-admin types
 */
const nonAdminTypes = ref([
  { label: 'Service Provider', id: PEOPLE_TYPES.CLINICIAN },
  { label: 'Case Manager', id: PEOPLE_TYPES.CASE_MANAGER },
  { label: 'Facilitator', id: PEOPLE_TYPES.FACILITATOR },
  { label: 'Parent', id: PEOPLE_TYPES.PARENT },
  { label: 'School Admin', id: PEOPLE_TYPES.SCHOOL_ADMIN },
]);

interface UserEditInterface {
  email?: string;
  externalId?: string;
  organizationId?: number;
  organization?: string;
  firstName: string;
  lastName: string;
  personTypeId: string | number;
  timezoneId?: string | number;
  timezone?: string;
}
/**
 * if eLuma tenant user we add these to the userTypes:
 * Billing Admin, Service Provider Supervisor,
 * Service Provider Admin,Support Admin and Super Admin.
 */
const eLumaUserTypes = ref([{ label: 'Billing Admin', id: PEOPLE_TYPES.BILLING_ADMIN }]);

/**
 * The data ref object used to create the request payload.
 */
const data = ref<UserEditInterface>({
  email: '',
  externalId: '',
  organizationId: '',
  organization: '',
  firstName: '',
  lastName: '',
  personTypeId: '',
  timezoneId: '',
  timezone: '',
});

const isStudent = computed<boolean>(() => {
  const personType = data.value.personTypeId;

  if (personType === PEOPLE_TYPES.STUDENT) {
    return usePersonTypes.isStudent(PEOPLE_TYPES.STUDENT);
  }

  return false;
});

const isBillingAdmin = computed(() => {
  return data.value.personTypeId === PEOPLE_TYPES.BILLING_ADMIN;
});

const editLoading = computed<boolean>(() => (isEdit.value ? !!profileStore.loading : false));

const modalLoading = ref(false);

/**
 * is the admin a super admin?
 */
const isSuperAdmin = computed<boolean>(() => authStore.acronymType === PEOPLE_TYPES.SUPER_ADMIN);

/**
 * is the admin a support admin?
 */
const isSupportAdmin = computed<boolean>(() => authStore.acronymType === PEOPLE_TYPES.SUPPORT_ADMIN);

/**
 * The required validation rules.
 */
const rules = computed(() => {
  return {
    email: { email },
    externalId: {
      requiredIfStudent: requiredIf(isStudent),
    },
    organization: {
      requiredIfStudent: requiredIf(isStudent),
    },
    firstName: { required },
    lastName: { required },
    personTypeId: { required },
    timezoneId: {},
  };
});

/**
 * Initialize the vuelidate instance.
 */
const v$ = useVuelidate(rules, data, { $externalResults });

const requiredIfStudent = computed(() => {
  v$.value.externalId.$touch();
  return isStudent.value ? 'required' : '';
});

/**
 * Throw all errors here for easier access.
 */
const errors = ref(null);

/**
 * Determines whether or not the form has been submitted.
 */
const submitted = ref<boolean>(false);

/**
 * Initialize everything required to build out the component.
 */
onMounted(() => init());

/**
 * master organization for adding eluma super, support and billing admins
 */
const masterOrganization = ref<OrganizationInterface>();
/**
 * Initialize the necessary data in order to build out the component.
 */
const init = async () => {
  modalLoading.value = true;
  if (isAdmin.value) masterOrganization.value = await orgStore.getOrganizationByName('eLuma');
  if (isEdit.value) await profileStore.getProfile(id.value as number);
  if (can([PERMISSIONS.PEOPLE_TYPES_INDEX])) await peopleStore.getPeopleTypes();
  if (can([PERMISSIONS.TIMEZONES_INDEX])) await timezoneStore.getTimezones();
  if (can([PERMISSIONS.ORGANIZATIONS_INDEX]) || isBillingAdmin.value) await orgStore.getParentsOrganizations();
  await defineDefaults();
  await createUserTypesArray();
  modalLoading.value = false;
};

/**
 * Define the request create payload.
 */
const payload = computed(() => {
  const value = data.value;
  const firstName = value.firstName.trim();
  const lastName = value.lastName.trim();
  return {
    person_type_id: findActualPersonTypeId(value.personTypeId as string),
    first_name: firstName,
    last_name: lastName,
    display_name: `${firstName} ${lastName}`,
    full_name: `${firstName} ${lastName}`,
    abbrev_name: `${firstName.charAt(0)}. ${lastName}`,
    external_id: value.externalId,
    email: value.email,
    timezone_id: value.timezoneId,
    timezone: value.timezone,
    organization_id: value.organizationId,
  };
});

/**
 * take the selected person type acronym and find the person type ID
 */
const findActualPersonTypeId = (personTypeAcronym: string) => {
  const type = peopleTypesStore.types.find((item) => item.acronym === personTypeAcronym);
  return type?.id;
};

/**
 * Define all default values that will pre-populate the form.
 */
const defineDefaults = async () => {
  const timezone = useDateTime().findTimezone();
  v$.value.$invalid = true;

  if (timezone) {
    data.value.timezoneId = timezone.id.toString();
    data.value.timezone = timezone.name;
  }

  if (isEdit.value) {
    setDataValues();
  }
};

/**
 * if we are editing we need to set the data values
 */
const setDataValues = () => {
  const value = profileStore.person;
  if (value) {
    data.value = {
      personTypeId: personTypeAcronym.value,
      firstName: value.first_name,
      lastName: value.last_name,
      email: value?.email,
      timezoneId: value?.timezone_id,
      timezone: value?.timezone,
    };
  }
};

/**
 * create the UserTypes array based on the values coming in, isEluma, and isAdmin
 */
const createUserTypesArray = () => {
  if (isAdmin.value) {
    if (isEdit.value && !isSuperAdmin.value) return;

    userTypes.value = supportAdminUserTypes.value;
    //if its a super admin then allow them to add super admins
    if (isSuperAdmin.value) userTypes.value = userTypes.value.concat(superAdminUserTypes.value);
  } else {
    userTypes.value = nonAdminTypes.value;
    if (authStore.currentInstanceIsMasterTenant && (isSuperAdmin.value || isSupportAdmin.value))
      userTypes.value = userTypes.value.concat(eLumaUserTypes.value);
  }
  //auto selet the first one in the array
  data.value.personTypeId = userTypes.value[0].id;
};

/**
 * get the key of the org tree select object for the ID
 */
const getOrganizationId = (val) => (data.value.organizationId = parseInt(Object.keys(val)));

/**
 * Get the field styles based on the specific key.
 */
const fieldStyles = (key: string, styles: string): string => {
  const validation =
    (v$.value[key].$invalid && submitted.value) || (errors.value && errors.value[key]) ? 'p-invalid' : '';
  return `${styles} ${validation}`;
};

/**
 * After a successful save, redirect the user to the new user account.
 */
const doSave = (response: ApiPromise) => {
  doToast();
  setTimeout(() => {
    const route = isAdmin.value ? `/admin/administrators/${response.id.toString()}` : `/user/${response.id.toString()}`;
    $routing.redirectTo(route);
  }, 3000);
};

/**
 * After a successful edit, close the modal
 */
const doUpdate = () => {
  if (isProfile.value) {
    authStore.getAuthUserProfile();
  }
  doToast('The admin has been updated');
  emit(EMIT.EDIT);
};

/**
 * Display the toast message.
 */
const doToast = (message = 'The new account has been saved', error = false) => {
  const summary = error ? 'Error' : 'Saved';
  authStore.doToast(message, summary, error);
};

/**
 * Do the following after a failed save request.
 * Reset the loading status indicator.
 * Set the error ref object with the error response.
 * Display the toast message.
 */
const doFail = (response: ApiPromise) => {
  submitted.value = false;
  doToast(response.data.message, true);
  emit(EMIT.CANCEL);
};

/**
 * Handle the cancellation of the user create modal.
 */
const onCancel = () => emit(EMIT.CANCEL);

/**
 * Handle the client side validation, followed by saving the
 * information for the new user account.
 */

const isPeopleTypeStudent = computed(() => {
  const peopleTypeStudent = peopleStore.peopleTypes.filter((peopleType) => peopleType.acronym === PEOPLE_TYPES.STUDENT);

  if (peopleTypeStudent.length > 0) {
    return payload.value.person_type_id === peopleTypeStudent[0].id;
  }

  return false;
});

const onSave = async () => {
  submitted.value = true;
  let payloadToRequest = payload.value;

  // Don't allow a submit event unless the fields are valid.
  if (!(await v$.value.$validate())) return;

  if (!isPeopleTypeStudent.value) {
    const {
      person_type_id,
      first_name,
      last_name,
      display_name,
      full_name,
      abbrev_name,
      external_id,
      email,
      timezone_id,
      timezone,
    } = payload.value;

    payloadToRequest = {
      person_type_id,
      first_name,
      last_name,
      display_name,
      full_name,
      abbrev_name,
      external_id,
      email,
      timezone_id,
      timezone,
    };
  }

  if (isEdit.value) {
    profileStore.id = id.value as number;
    profileStore
      .updateGeneralInfo(payloadToRequest as GeneralProfileInterface)
      .then((response) => doUpdate(response))
      .catch((response) => doFail(response));
  } else {
    if (isAdmin.value) payloadToRequest.organization_id = masterOrganization.value?.id;

    peopleStore
      .createPerson(payloadToRequest as PersonCreateInterface)
      .then((response) => doSave(response))
      .catch((response) => {
        doFail(response);
        displayModal.value = false;
      });
  }
};

/**
 * Handle the filtering of timezones based on zipcode.
 * The search will not begin until at least 4 characters
 * have been entered.
 */
const onTimezoneFilter = (event) => {
  const zipcode = event.value;
  const hasValue = zipcode.length >= 4;

  if (hasValue) timezoneStore.searchTimezonesByZipcode(zipcode);
};

const selectPersonType = (id: string | number) => {
  data.value.personTypeId = id;
};
</script>

<template>
  <Dialog
    v-model:visible="displayModal"
    :modal="true"
    :header="header"
    :draggable="false"
    :breakpoints="{ '960px': '75vw', '640px': '100vw' }"
    :style="{ maxWidth: '600px', width: '100%' }"
    @hide="onCancel"
  >
    <form v-if="!editLoading && !modalLoading" class="card">
      <AppSelectButton
        :items="userTypes"
        :selected-item="data.personTypeId"
        class="pb-3"
        @select-item="selectPersonType($event.id)"
      />
      <div class="field">
        <label class="required" for="firstName">First Name</label>
        <InputText
          id="firstName"
          v-model="v$.firstName.$model"
          type="text"
          placeholder="First Name"
          :disabled="submitted"
          :class="fieldStyles('firstName', 'w-full')"
        />
        <small v-if="errors && errors.firstName" class="p-error">{{ errors.firstName[0] }}</small>
      </div>
      <div class="field">
        <label class="required" for="lastName">Last Name</label>
        <InputText
          id="lastName"
          v-model="v$.lastName.$model"
          type="text"
          placeholder="Last Name"
          :disabled="submitted"
          :class="fieldStyles('lastName', 'w-full')"
        />
        <small v-if="errors && errors.lastName" class="p-error">{{ errors.lastName[0] }}</small>
      </div>
      <div v-if="isStudent || isBillingAdmin" class="field">
        <label :class="requiredIfStudent" for="org">Organization</label>
        <TreeSelect
          id="org"
          v-model="v$.organization.$model"
          :options="orgStore.organizationParents"
          selection-mode="single"
          :loading="orgStore.loading"
          placeholder="Select organization"
          :disabled="submitted"
          class="w-full"
          @change="getOrganizationId"
        />
        <small v-if="v$.organization.$error" class="p-error">Organization is required</small>
      </div>
      <div v-if="!isAdmin" class="field">
        <label :class="requiredIfStudent" for="externalId">{{ isStudent ? 'SSID' : 'External ID' }}</label>
        <InputText
          id="externalId"
          v-model="v$.externalId.$model"
          type="text"
          placeholder="example: 123456789"
          :disabled="submitted"
          :class="fieldStyles('externalId', 'w-full')"
        />
        <small v-if="errors && errors.externalId" class="p-error">{{ errors.externalId[0] }}</small>
        <small v-for="error of v$.externalId.$externalResults" :key="error.$uid" class="p-error">
          {{ error.$message }}
        </small>
      </div>
      <div class="field">
        <label for="email">Email</label>
        <InputText
          id="email"
          v-model="v$.email.$model"
          type="text"
          placeholder="example@email.com"
          :disabled="submitted"
          class="w-full"
        />
        <small v-if="errors && errors.email" class="p-error">{{ errors.email[0] }}</small>
      </div>
      <div class="field">
        <label for="timezone">Timezone</label>
        <Dropdown
          id="timezone"
          v-model="v$.timezoneId.$model"
          :options="timezoneStore.timezones"
          option-label="name"
          option-value="id"
          :loading="timezoneStore.loading"
          placeholder="Select timezone"
          :filter="true"
          filter-placeholder="Search by zipcode"
          :filter-fields="['zipcode', 'name']"
          :disabled="submitted"
          class="w-full"
          @filter="onTimezoneFilter"
        />
      </div>
    </form>
    <Loader v-else :relative="true" class="flex justify-content-center align-items-center h-24rem" />
    <template #footer>
      <div class="flex justify-content-end">
        <AppButton type="secondary" text="Cancel" class="mr-2" :disabled="submitted" @click="onCancel" />
        <AppButton type="primary" :text="saveButtonText" :disabled="v$.$invalid || submitted" @click="onSave" />
      </div>
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
.user-menu {
  width: 100%;

  :deep(.p-button) {
    height: 24px;
    background-color: white;
    border-radius: 4px !important;
    padding: 3px 8px;
    align-items: center;
    margin-right: 4px;
    margin-top: 4px;
    border: 1px solid white;
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;

    &.p-highlight,
    :hover {
      color: $greenDark;
      background-color: $greenLight;
      border-color: $greenLight;
    }
  }
}

:deep(.p-selectbutton) {
  .p-button:not(.p-disabled):not(.p-highlight):hover {
    color: $greenDark;
    background-color: $greenLight;
    border-color: $greenLight;
  }
}
</style>
